import dayjs from "dayjs";

/**
 * Query names `GET /api/v1/orders` takes for the order-list filters. `keyword`
 * is one box matching either an order number or an order name; the delivery pair
 * takes epoch seconds. Both order lists read them from here.
 */
export const KEYWORD_PARAM = "keyword";
export const DELIVERED_FROM_PARAM = "last_image_delivered_from";
export const DELIVERED_TO_PARAM = "last_image_delivered_to";

/**
 * What `sort_by` takes to order the list by the delivery moment. Sorting by the
 * order date needs no `sort_by` at all — that is the endpoint's default field —
 * so this is the only value the lists ever send.
 */
export const DELIVERED_SORT_FIELD = "last_image_delivered_at";

/**
 * Turns AntD's sorter argument into the pair of query values the endpoint wants.
 *
 * AntD's third click on a column "cancels" the sort and hands back an empty
 * sorter — no order, and not even the column it came from. The list is never
 * unsorted though (the endpoint always orders by something), so that click
 * flips the direction of the sort in effect instead. Returning the previous
 * state there would leave the header stuck: the arrow is controlled from this
 * state, so an unchanged state means every further click cancels again.
 *
 * Paging is safe from that reading — AntD passes the active sort along with it.
 */
export const resolveTableSort = (
      sorter: any,
      previous: { sort: "asc" | "desc"; sortBy: string | null },
): { sort: "asc" | "desc"; sortBy: string | null } =>
{
      const active = Array.isArray(sorter) ? sorter[0] : sorter;

      if (!active?.order)
      {
            return {
                  sort: previous.sort === "asc" ? "desc" : "asc",
                  sortBy: previous.sortBy,
            };
      }

      return {
            sort: active.order === "ascend" ? "asc" : "desc",
            // Anything other than the delivery column is the default field.
            sortBy:
                  active.columnKey === DELIVERED_SORT_FIELD
                        ? DELIVERED_SORT_FIELD
                        : null,
      };
};

/**
 * How long typing has to stop before the keyword box searches on its own.
 * Deliberately long: an order number is ~20 characters, and firing a request
 * per pause mid-code just spams the endpoint. Enter searches straight away.
 */
export const KEYWORD_DEBOUNCE_MS = 3000;

// Hàm chuyển từ thập phân sang DMS
export function decimalToDMS(
      decimal?: any,
      directionPositive?: any,
      directionNegative?: any
)
{
      const degrees = Math.floor(Math.abs(decimal));
      const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = Math.round((minutesDecimal - minutes) * 60);

      const direction = decimal >= 0 ? directionPositive : directionNegative;
      return `${degrees}° ${minutes}′ ${seconds}″ ${direction}`;
}

/**
 * Renders `last_image_delivered_at` for the order lists. The field is only set
 * once images have actually been handed over, so most rows have nothing to show
 * — those stay blank instead of rendering a placeholder date. Both epoch numbers
 * and ISO strings are accepted because the endpoint isn't consistent about
 * which it sends.
 */
export function formatDeliveredAt(value: unknown): string
{
      if (value === null || value === undefined || value === "") return "";

      const parsed =
            typeof value === "number"
                  // A 10-digit epoch is seconds; anything longer is already ms.
                  ? dayjs(value < 1e11 ? value * 1000 : value)
                  : dayjs(value as string);

      return parsed.isValid() ? parsed.format("HH:mm DD/MM/YYYY") : "";
}

/**
 * How wide the table wants to be, from the widths its columns ask for.
 *
 * Handed to AntD as `scroll.x`: a container wider than this shows no scrollbar
 * at all, and a narrower one scrolls sideways instead of crushing every column
 * into a few characters. Computed rather than hardcoded so it follows whenever a
 * column's width is tuned.
 */
export const sumColumnWidths = (
      columns: { width?: string | number }[],
      /** Columns that state no width still take up room. */
      fallback = 160,
): number =>
      (columns ?? []).reduce((total, column) =>
      {
            const width = Number(column?.width);

            return total + (Number.isFinite(width) && width > 0 ? width : fallback);
      }, 0);
